import format from "date-fns/format"
import parseISO from "date-fns/parseISO"
import PropTypes from "prop-types"
import React, { useCallback, useEffect, useState } from "react"

import BasePopover from "src/components/BasePopover"
import Chips from "src/components/Chips"
import Form from "src/components/Form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "src/components/Select"

import useDebounce from "src/hooks/use_debounce"

const STATUS_OPTIONS_BY_TAB = {
  unsent: ["Unsent", "In House Only", "Processing", "Failed"],
  completed: [
    "Signed",
    "Completed In House",
    "Boater Declined",
    "Voided",
    "Canceled",
  ],
}

// deep_camelize lowercases the first character of hash keys,
// so status display names like "Unsent" become "unsent" in the response.
const getStatusCount = (statusCounts, status) => {
  if (!statusCounts) return 0
  const camelizedKey = status.charAt(0).toLowerCase() + status.slice(1)
  return statusCounts[camelizedKey] ?? statusCounts[status] ?? 0
}

const ContractQuoteFilters = ({ table, filterOptions }) => {
  const statusCounts = filterOptions?.statusCounts
  const [searchInputValue, setSearchInputValue] = useState(
    table.filters.search || ""
  )
  const [inMarinaStartDate, setInMarinaStartDate] = useState(
    table.filters.inMarinaStartDate
      ? parseISO(table.filters.inMarinaStartDate)
      : null
  )
  const [inMarinaEndDate, setInMarinaEndDate] = useState(
    table.filters.inMarinaEndDate
      ? parseISO(table.filters.inMarinaEndDate)
      : null
  )

  const [debouncedSetSearch] = useDebounce((value) => {
    table.setFilter("search", value)
  })

  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value
      setSearchInputValue(value)
      debouncedSetSearch(value.trim())
    },
    [debouncedSetSearch]
  )

  const handleStatusChange = (values) => {
    table.setFilter("statuses", values)
  }

  const handleInMarinaDateChange = (field, date) => {
    const formatted = date ? format(date, "yyyy-MM-dd") : null

    if (field === "inMarinaStartDate") {
      setInMarinaStartDate(date)
      if (!date) {
        setInMarinaEndDate(null)
        table.setFilters({ inMarinaStartDate: null, inMarinaEndDate: null })
      } else {
        table.setFilter("inMarinaStartDate", formatted)
      }
    } else {
      setInMarinaEndDate(date)
      if (!date) {
        setInMarinaStartDate(null)
        table.setFilters({ inMarinaStartDate: null, inMarinaEndDate: null })
      } else {
        table.setFilter("inMarinaEndDate", formatted)
      }
    }
  }

  // Sync local state when filters reset (e.g. tab switch)
  useEffect(() => {
    setSearchInputValue(table.filters.search || "")
    setInMarinaStartDate(
      table.filters.inMarinaStartDate
        ? parseISO(table.filters.inMarinaStartDate)
        : null
    )
    setInMarinaEndDate(
      table.filters.inMarinaEndDate
        ? parseISO(table.filters.inMarinaEndDate)
        : null
    )
  }, [
    table.filters.search,
    table.filters.inMarinaStartDate,
    table.filters.inMarinaEndDate,
  ])

  const buildChips = () => {
    const chips = []

    if (table.filters.search) {
      chips.push({
        key: "search",
        text: table.filters.search,
        onClick: () => {
          table.clearFilter("search")
          setSearchInputValue("")
        },
      })
    }

    if (table.filters.statuses?.length > 0) {
      table.filters.statuses.forEach((status) => {
        chips.push({
          key: `status-${status}`,
          text: status,
          onClick: () =>
            table.setFilter(
              "statuses",
              table.filters.statuses.filter((s) => s !== status)
            ),
        })
      })
    }

    if (table.filters.inMarinaStartDate && table.filters.inMarinaEndDate) {
      const start = format(
        parseISO(table.filters.inMarinaStartDate),
        "MM/dd/yyyy"
      )
      const end = format(parseISO(table.filters.inMarinaEndDate), "MM/dd/yyyy")
      chips.push({
        key: "inMarina",
        text: `In marina: ${start} – ${end}`,
        onClick: () => {
          setInMarinaStartDate(null)
          setInMarinaEndDate(null)
          table.setFilters({ inMarinaStartDate: null, inMarinaEndDate: null })
        },
      })
    }

    return chips
  }

  const currentTab = table.filters.tab
  const statusOptions = STATUS_OPTIONS_BY_TAB[currentTab] || []
  const chips = buildChips()

  return (
    <div className="space-y-2 py-4 print:hidden">
      <div className="flex w-full flex-wrap gap-2">
        <div className="w-full md:w-[336px]">
          <Form.IconTextField
            inputMode="text"
            name="contractQuoteSearch"
            onChange={handleSearchChange}
            placeholder="Search by name, email, boat, or ID"
            type="text"
            value={searchInputValue}
            position="left"
            icon={<i className="fa-kit fa-search-mdi text-xl text-gray-400" />}
          />
        </div>
        <div className="flex gap-2">
          {statusOptions.length > 0 && (
            <Select
              multiple
              value={table.filters.statuses}
              onValueChange={handleStatusChange}
            >
              <SelectTrigger variant="button">Status</SelectTrigger>
              <SelectContent>
                {statusOptions.map((status) => {
                  const count = getStatusCount(statusCounts, status)
                  return (
                    <SelectItem
                      key={status}
                      value={status}
                      disabled={count === 0}
                    >
                      {status} ({count})
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
          )}
          <BasePopover label="In Marina">
            <div className="space-y-2 p-4">
              <div className="flex flex-col">
                <Form.Label htmlFor="inMarinaStartDate">Start Date</Form.Label>
                <Form.DatePicker
                  value={inMarinaStartDate}
                  onChange={(date) =>
                    handleInMarinaDateChange("inMarinaStartDate", date)
                  }
                  id="inMarinaStartDate"
                  maxDate={inMarinaEndDate}
                  todayButton="Today"
                  portalId={null}
                />
              </div>
              <div className="flex flex-col">
                <Form.Label htmlFor="inMarinaEndDate">End Date</Form.Label>
                <Form.DatePicker
                  value={inMarinaEndDate}
                  onChange={(date) =>
                    handleInMarinaDateChange("inMarinaEndDate", date)
                  }
                  id="inMarinaEndDate"
                  minDate={inMarinaStartDate}
                  todayButton="Today"
                  portalId={null}
                />
              </div>
            </div>
          </BasePopover>
        </div>
      </div>
      {chips.length > 0 && (
        <Chips
          onReset={() => {
            table.setFilters({
              search: "",
              statuses: [],
              inMarinaStartDate: null,
              inMarinaEndDate: null,
            })
            setSearchInputValue("")
            setInMarinaStartDate(null)
            setInMarinaEndDate(null)
          }}
        >
          {chips.map((chip) => (
            <Chips.Chip
              key={chip.key}
              text={chip.text}
              onClick={chip.onClick}
            />
          ))}
        </Chips>
      )}
    </div>
  )
}

ContractQuoteFilters.propTypes = {
  table: PropTypes.object.isRequired,
  filterOptions: PropTypes.object,
}

export default ContractQuoteFilters
